import { useCallback, useEffect, useState } from 'react';
import { ReactNode } from 'react';
import { Question as QuestionFull } from 'types';
import { indexToLetter } from 'utils';
import { FormControlLabel, RadioGroup, Radio, Stack, Button, TextField, Box } from '@mui/material';
import { compareTwoStrings } from 'string-similarity';
import { Question } from './question';

interface Props {
    question: QuestionFull;
    children?: ReactNode;
    onAnswer?: (isAnswerCorrect: boolean, question: QuestionFull, userAnswer?: string) => void;
    similarityThreshold?: number;
    initAnswer?: string;
    disabled?: boolean;
    showQuestion?: boolean;
}

export const Answer = (props: Props) => {
    const [answer, setAnswer] = useState<string>('');
    const [questionVisible] = useState(props.showQuestion ?? true);
    const isMultipleChoice = props.question.answers.length > 1;

    useEffect(() => {
        setAnswer(props.initAnswer ?? '');
    }, [props]);

    const onMultipleChoiceAnswerChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setAnswer(value);
    };

    const onSubmitAnswer = useCallback(() => {
        const correctAnswer = props.question.answers.find((a) => a.isCorrect)!;
        if (isMultipleChoice) {
            props.onAnswer!(correctAnswer.answer === answer, props.question, answer);
            return;
        }

        const similarity = compareTwoStrings(correctAnswer.answer!.toLowerCase(), answer!.toLowerCase());
        const threshold = props.similarityThreshold ?? 0.75;
        props.onAnswer!(similarity >= threshold, props.question, answer);
    }, [answer, isMultipleChoice, props.onAnswer, props.question, props.similarityThreshold]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !!answer) {
                onSubmitAnswer();
            }
        };
        if (props.onAnswer) {
            // attach the event listener
            document.addEventListener('keydown', handleKeyPress);
        }
        // remove the event listener
        return () => {
            if (props.onAnswer) {
                document.removeEventListener('keydown', handleKeyPress);
            }
        };
    }, [answer, onSubmitAnswer, props.onAnswer]);

    const onSkipQuestion = () => {
        props.onAnswer!(false, props.question);
    };

    const renderAnswerButtons = (
        <Stack className="answer-buttons" direction="row" spacing={2}>
            <Button variant="contained" className="incorrect" onClick={onSkipQuestion} disabled={!!props.disabled}>
                Skip
            </Button>
            <Button variant="contained" className="correct" onClick={onSubmitAnswer} disabled={!answer || !!props.disabled}>
                Submit
            </Button>
        </Stack>
    );

    const renderAnswersRadioButtons = (answers: Pick<QuestionFull, 'answers'>) => {
        return (
            <Stack direction="column" spacing={2} className="answer answers radiobuttons">
                <RadioGroup name="multiple-choice-answers" onChange={onMultipleChoiceAnswerChange}>
                    {answers.answers.map((answerItem, index) => (
                        <FormControlLabel
                            key={`answer-${index}`}
                            value={answerItem.answer}
                            control={<Radio checked={answerItem.answer === answer} disabled={props.disabled} />}
                            label={`${indexToLetter(index)}) ${answerItem.answer}`}
                        />
                    ))}
                </RadioGroup>
                {renderAnswerButtons}
            </Stack>
        );
    };

    const renderAnswerInput = () => (
        <Stack direction="column" spacing={2} className="answer answers input">
            <TextField label="Answer" value={answer} onChange={(event) => setAnswer(event.target.value)} disabled={props.disabled} />
            {renderAnswerButtons}
        </Stack>
    );

    const renderAnswer = (answers: Pick<QuestionFull, 'answers'>) => {
        if (props.onAnswer) {
            if (isMultipleChoice) {
                return renderAnswersRadioButtons(answers);
            }

            return renderAnswerInput();
        }

        if (isMultipleChoice) {
            return (
                <ul className="answer answers correct_answer">
                    {answers.answers.map((answer, index) => (
                        <li key={`answer-${index}`} className={answer.isCorrect ? 'correct_item' : ''}>
                            {indexToLetter(index)}) {answer.answer}
                        </li>
                    ))}
                </ul>
            );
        }

        return <div className="answer correct_answer correct_item">{answers.answers.map((answer) => answer.answer)}</div>;
    };

    return questionVisible ? (
        <Question question={props.question} baseClassName="answer" showMultipleChoiceOptions={false}>
            {renderAnswer(props.question)}
            {props.children}
        </Question>
    ) : (
        <Box className='answer'>
            {renderAnswer(props.question)}
            {props.children}
        </Box>
    );
};
